/**
 * Trunnion AI - Workflow Serialization
 * JSON/YAML conversion for workflow definitions.
 */

import yaml from 'js-yaml';
import type { WorkflowDefinition } from '@/types';

export function workflowToJson(workflow: WorkflowDefinition): string {
  return JSON.stringify(workflow, null, 2);
}

export function workflowFromJson(json: string): WorkflowDefinition {
  const parsed = JSON.parse(json);
  return parsed as WorkflowDefinition;
}

export function workflowToYaml(workflow: WorkflowDefinition): string {
  return yaml.dump(workflow, {
    indent: 2,
    lineWidth: 120,
    noRefs: true,
    sortKeys: false,
  });
}

export function workflowFromYaml(yamlStr: string): WorkflowDefinition {
  const parsed = yaml.load(yamlStr);
  return parsed as WorkflowDefinition;
}

/**
 * Detect format and parse accordingly
 */
export function parseWorkflow(content: string): WorkflowDefinition {
  const trimmed = content.trim();
  if (trimmed.startsWith('{')) {
    return workflowFromJson(trimmed);
  }
  return workflowFromYaml(trimmed);
}

/**
 * Export workflow in specified format
 */
export function exportWorkflow(
  workflow: WorkflowDefinition,
  format: 'json' | 'yaml',
): string {
  if (format === 'yaml') {
    return workflowToYaml(workflow);
  }
  return workflowToJson(workflow);
}

/**
 * Create a download blob for the exported workflow
 */
export function createExportBlob(
  content: string,
  format: 'json' | 'yaml',
): Blob {
  const mimeType = format === 'yaml' ? 'text/yaml' : 'application/json';
  return new Blob([content], { type: mimeType });
}

/**
 * Trigger file download
 */
export function downloadFile(
  blob: Blob,
  filename: string,
): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
